import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { MIcon, ensureMaterialSymbols } from '../components/MIcon';
import { localizedPath, localizedUrl } from '../seo/routes';
import { resolveMeta } from '../seo/meta';
import { CLINIC, breadcrumbLd, dentistRef, faqLd, medicalWebPageLd } from '../seo/clinic';

// === Notfall-Spoke-Template (SEO_NOTFALL_PLAN.md §5.3) ===
// EIN wiederverwendbares Template für alle Notfall-Unterseiten; pro Spoke nur
// eine dünne Wrapper-Datei mit SpokeConfig. Texte kommen vollständig aus
// translations.emergency.<namespace> (SpokeContent-Schema, siehe Kontrakt).
// Design: ds-Tokens aus redesign emergency/DESIGN.md — Notfall-Rot NUR für
// Anruf-CTAs, Grün für WhatsApp. Mobile-first: Notfall-Traffic ist mobil.

export interface SpokeConfig {
  routeKey: string;                       // z.B. 'emergency-broken-tooth' (Seo, localizedPath, canonical)
  namespace: 'brokenTooth' | 'toothache' | 'tourists' | 'abscess';  // translations.emergency[namespace]
  warnboxVariant?: 'countdown' | 'danger'; // brokenTooth: countdown · abscess: danger · sonst keine
  medicalCondition: string;               // schema.org about, z.B. 'Dental trauma'
}

// Lose Typen fürs Rendering — die Sprachdateien werden parallel gefüllt und
// dürfen zur Compile-/Laufzeit unvollständig sein (kein Key-Level-Fallback im
// LanguageContext!), daher überall Optional Chaining.
type SectionItem = { title?: string; body: string };
type Section = {
  icon?: string;
  title: string;
  intro?: string;
  items?: SectionItem[];
  table?: { headers: string[]; rows: string[][] };
};
type FaqItem = { q: string; a: string };

const EmergencySpokePage: React.FC<SpokeConfig> = ({
  routeKey,
  namespace,
  warnboxVariant,
  medicalCondition,
}) => {
  const { translations, language } = useLanguage();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    ensureMaterialSymbols();
  }, []);

  const t = translations.emergency?.[namespace];
  const common = translations.emergency?.common;
  if (!t) return null;

  const hubPath = localizedPath('emergency', language);
  const bookingPath = `${localizedPath('contact', language)}?booking=true&treatment=emergency`;
  const pageUrl = localizedUrl(routeKey, language);
  const meta = resolveMeta(routeKey, language);

  // JSON-LD: MedicalWebPage (mit Dentist-Referenz per @id statt NAP-Kopie),
  // BreadcrumbList Home → Hub → Spoke, FAQPage 1:1 zum sichtbaren Accordion.
  const medicalLd = {
    ...medicalWebPageLd({
      name: meta?.title ?? t.h1 ?? '',
      description: meta?.description ?? t.heroIntro ?? '',
      url: pageUrl,
      about: medicalCondition,
      lang: language,
    }),
    provider: dentistRef(),
  };
  const crumbsLd = breadcrumbLd([
    { name: translations.nav?.home ?? 'Home', url: localizedUrl('home', language) },
    { name: common?.breadcrumbHome ?? 'Notdienst', url: localizedUrl('emergency', language) },
    { name: t.breadcrumb ?? t.h1 ?? '', url: pageUrl },
  ]);
  const faqItems: FaqItem[] = t.faq?.items ?? [];

  const isDanger = warnboxVariant === 'danger';

  return (
    <main className="bg-ds-surface text-ds-on-background font-ds-body pb-20 md:pb-0">
      <Seo pageKey={routeKey} />
      <JsonLd data={medicalLd} />
      <JsonLd data={crumbsLd} />
      {faqItems.length > 0 && <JsonLd data={faqLd(faqItems)} />}

      {/* === Hero === */}
      <section className="bg-ds-primary text-ds-clinical-white pt-32 lg:pt-36 pb-12 md:pb-16">
        <div className="max-w-container-max mx-auto px-gutter">
          {/* Breadcrumb: Home → Hub → Spoke */}
          <nav aria-label="Breadcrumb" className="mb-6 text-[13px] text-ds-clinical-white/60">
            <ol className="flex flex-wrap items-center gap-1">
              <li>
                <Link to={localizedPath('home', language)} className="hover:text-ds-clinical-white transition-colors">
                  {translations.nav?.home ?? 'Home'}
                </Link>
              </li>
              <li aria-hidden="true" className="px-1">/</li>
              <li>
                <Link to={hubPath} className="hover:text-ds-clinical-white transition-colors">
                  {common?.breadcrumbHome}
                </Link>
              </li>
              <li aria-hidden="true" className="px-1">/</li>
              <li aria-current="page" className="text-ds-clinical-white/90">{t.breadcrumb}</li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <h1 className="font-ds-display text-[32px] leading-tight md:text-[44px] mb-5">
              {t.h1}
            </h1>
            <p className="text-[17px] md:text-[18px] leading-7 text-ds-clinical-white/85 mb-8">
              {t.heroIntro}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={`tel:${CLINIC.phone}`}
                className="flex items-center justify-center gap-3 bg-ds-emergency-red text-ds-clinical-white px-7 py-4 rounded-xl font-bold text-[17px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
              >
                <MIcon name="call" />
                {common?.callNow ?? CLINIC.phoneDisplay}
              </a>
              <a
                href={CLINIC.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-ds-success-green text-ds-clinical-white px-7 py-4 rounded-xl font-bold text-[17px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
              >
                <MIcon name="chat" />
                {common?.whatsappCta}
              </a>
            </div>
            {t.heroNote && (
              <p className="mt-5 text-[14px] text-ds-clinical-white/70">{t.heroNote}</p>
            )}
          </div>
        </div>
      </section>

      {/* === Warnbox (above the fold) — countdown: amber/dringlich · danger: rot mit 112/061 === */}
      {warnboxVariant && t.warnbox && (
        <section className="px-gutter -mt-6 relative z-10">
          <div
            role="alert"
            className={`max-w-container-max mx-auto rounded-2xl border-l-4 p-6 md:p-8 shadow-ds-card ${
              isDanger
                ? 'bg-ds-error-container border-ds-emergency-red'
                : 'bg-amber-50 border-amber-500'
            }`}
          >
            <div className="flex items-start gap-4">
              <MIcon
                name={isDanger ? 'emergency' : 'timer'}
                className={`text-[32px] flex-shrink-0 ${isDanger ? 'text-ds-emergency-red' : 'text-amber-600'}`}
                filled
              />
              <div>
                <h2 className={`font-bold text-[19px] md:text-[21px] mb-2 ${isDanger ? 'text-ds-on-error-container' : 'text-amber-900'}`}>
                  {t.warnbox.title}
                </h2>
                <p className={`text-[15px] md:text-[16px] leading-6 ${isDanger ? 'text-ds-on-error-container/90' : 'text-amber-900/90'}`}>
                  {t.warnbox.body}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* === Inhalts-Sektionen (H2 + intro + Karten/Liste + optionale Tabelle) === */}
      {(t.sections as Section[] | undefined)?.map((section, i) => (
        <section key={i} className="py-10 md:py-14 px-gutter">
          <div className="max-w-container-max mx-auto">
            <div className="flex items-center gap-3 mb-4">
              {section.icon && (
                <span className="w-11 h-11 bg-ds-secondary-fixed rounded-xl flex items-center justify-center text-ds-primary flex-shrink-0">
                  <MIcon name={section.icon} className="text-[26px]" />
                </span>
              )}
              <h2 className="font-ds-display text-[26px] md:text-[32px] leading-tight text-ds-primary">
                {section.title}
              </h2>
            </div>
            {section.intro && (
              <p className="text-ds-on-surface-variant text-[16px] md:text-[18px] leading-7 mb-8 max-w-3xl">
                {section.intro}
              </p>
            )}

            {section.items && section.items.length > 0 && (
              section.items.some((item) => item.title) ? (
                // Items mit Titel → Karten-Raster
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {section.items.map((item, j) => (
                    <div
                      key={j}
                      className="bg-ds-clinical-white rounded-2xl p-6 shadow-ds-card border border-ds-outline-variant/20"
                    >
                      {item.title && (
                        <h3 className="font-semibold text-ds-primary text-[18px] mb-2">{item.title}</h3>
                      )}
                      <p className="text-ds-on-surface-variant text-[15px] leading-6">{item.body}</p>
                    </div>
                  ))}
                </div>
              ) : (
                // Items ohne Titel → Checkliste
                <ul className="space-y-3 max-w-3xl">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <MIcon name="check_circle" className="text-ds-secondary mt-0.5 flex-shrink-0" filled />
                      <span className="text-ds-on-surface text-[15px] md:text-[16px] leading-6">{item.body}</span>
                    </li>
                  ))}
                </ul>
              )
            )}

            {section.table && (
              <div className="mt-8 overflow-x-auto rounded-2xl shadow-ds-card border border-ds-outline-variant/20">
                <table className="min-w-full bg-ds-clinical-white text-left text-[14px] md:text-[15px]">
                  <thead>
                    <tr className="bg-ds-primary text-ds-clinical-white">
                      {section.table.headers?.map((h, j) => (
                        <th key={j} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.rows?.map((row, j) => (
                      <tr key={j} className={j % 2 ? 'bg-ds-surface-container-low' : ''}>
                        {row.map((cell, k) => (
                          <td key={k} className="px-4 py-3 align-top text-ds-on-surface leading-6">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      ))}

      {/* === "So lösen wir das bei SeaDent" === */}
      {t.solve && (
        <section className="py-12 md:py-16 bg-ds-primary text-ds-clinical-white px-gutter">
          <div className="max-w-container-max mx-auto">
            <h2 className="font-ds-display text-[26px] md:text-[32px] leading-tight mb-4">
              {t.solve.title}
            </h2>
            {t.solve.intro && (
              <p className="text-ds-clinical-white/80 text-[16px] md:text-[18px] leading-7 mb-10 max-w-3xl">
                {t.solve.intro}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {(t.solve.points as SectionItem[] | undefined)?.map((point, i) => (
                <div key={i} className="bg-ds-clinical-white/5 border border-ds-clinical-white/10 rounded-2xl p-6">
                  <h3 className="font-semibold text-[18px] mb-2 text-ds-secondary-fixed-dim">{point.title}</h3>
                  <p className="text-ds-clinical-white/80 text-[15px] leading-6">{point.body}</p>
                </div>
              ))}
            </div>
            {/* Interne Lösungs-Links (Composite, Technologien, …) — keyword-tragende Ankertexte */}
            <div className="flex flex-wrap gap-3">
              {(t.solve.links as Array<{ text: string; routeKey: string }> | undefined)?.map((link, i) => (
                <Link
                  key={i}
                  to={localizedPath(link.routeKey, language)}
                  className="inline-flex items-center gap-2 bg-ds-clinical-white/10 hover:bg-ds-clinical-white/20 border border-ds-clinical-white/20 px-5 py-3 rounded-xl text-[14px] font-semibold transition-colors"
                >
                  {link.text}
                  <MIcon name="arrow_forward" className="text-[18px]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === Mid-CTA === */}
      {common?.midCta && (
        <section className="py-12 md:py-16 px-gutter">
          <div className="max-w-container-max mx-auto bg-ds-clinical-white rounded-3xl shadow-ds-card-lg p-8 md:p-12 text-center">
            <h2 className="font-ds-display text-[24px] md:text-[30px] text-ds-primary mb-3">
              {common.midCta.title}
            </h2>
            <p className="text-ds-on-surface-variant text-[16px] leading-7 mb-8 max-w-2xl mx-auto">
              {common.midCta.body}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <a
                href={`tel:${CLINIC.phone}`}
                className="flex items-center justify-center gap-2 bg-ds-emergency-red text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[16px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
              >
                <MIcon name="call" />
                {common.callNow ?? CLINIC.phoneDisplay}
              </a>
              <Link
                to={bookingPath}
                className="flex items-center justify-center gap-2 bg-ds-primary text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[16px] hover:bg-ds-secondary active:scale-[0.98] transition-all"
              >
                <MIcon name="calendar_month" />
                {common.midCta.button}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* === FAQ (Accordion nach CompositePage-Muster) === */}
      {faqItems.length > 0 && (
        <section className="py-12 md:py-16 px-gutter bg-ds-surface-container-low">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-ds-display text-[26px] md:text-[32px] text-ds-primary text-center mb-10">
              {t.faq?.title}
            </h2>
            <div className="space-y-3">
              {faqItems.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} className="bg-ds-clinical-white rounded-xl overflow-hidden border border-ds-outline-variant/30">
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full px-5 md:px-6 py-4 md:py-5 flex justify-between items-center text-left hover:bg-ds-surface-container transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className="text-ds-primary font-semibold text-[16px] md:text-[17px] pr-4">{item.q}</span>
                      <MIcon
                        name="expand_more"
                        className={`text-ds-secondary flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 md:px-6 pb-5 text-ds-on-surface-variant text-[15px] leading-relaxed">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* === Location-Kurzblock + Rezensionen (sichtbarer Text, kein Rating-Markup) === */}
      {(common?.locationLine || common?.calloutReviews) && (
        <section className="py-10 md:py-12 px-gutter">
          <div className="max-w-container-max mx-auto bg-ds-clinical-white rounded-2xl shadow-ds-card border border-ds-outline-variant/20 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            {common?.locationLine && (
              <div className="flex items-start gap-3 flex-1">
                <MIcon name="location_on" className="text-ds-secondary mt-0.5 flex-shrink-0" />
                <p className="text-ds-on-surface text-[15px] leading-6">{common.locationLine}</p>
              </div>
            )}
            {common?.calloutReviews && (
              <div className="flex items-center gap-3">
                <MIcon name="star" className="text-amber-500" filled />
                <p className="text-ds-primary font-semibold text-[15px] whitespace-nowrap">{common.calloutReviews}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* === Autorenbox (E-E-A-T: medizinisch geprüft, Colegiado-Nr.) === */}
      {common?.authorBox && (
        <section className="px-gutter pb-4">
          <div className="max-w-container-max mx-auto border-l-4 border-ds-secondary bg-ds-surface-container-low rounded-r-xl p-5 md:p-6">
            <p className="text-[13px] uppercase tracking-widest font-semibold text-ds-secondary mb-1">
              {common.authorBox.label}
            </p>
            <p className="text-ds-primary font-bold text-[16px]">{common.authorBox.name}</p>
            <p className="text-ds-on-surface-variant text-[14px]">{common.authorBox.credential}</p>
            <p className="text-ds-on-surface-variant text-[13px] mt-1">
              {common.authorBox.reviewedLabel} {common.authorBox.reviewedDate}
            </p>
          </div>
        </section>
      )}

      {/* === Final-CTA + Rückverlinkung zum Hub === */}
      {t.finalCta && (
        <section className="py-14 md:py-20 px-gutter bg-ds-surface-container-highest">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-ds-display text-[26px] md:text-[32px] text-ds-primary mb-4">
              {t.finalCta.title}
            </h2>
            <p className="text-ds-on-surface-variant text-[16px] md:text-[18px] leading-7 mb-10">
              {t.finalCta.body}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-8">
              <a
                href={`tel:${CLINIC.phone}`}
                className="flex items-center justify-center gap-2 bg-ds-emergency-red text-ds-clinical-white px-9 py-5 rounded-2xl font-bold text-[18px] shadow-xl hover:scale-[1.03] active:scale-[0.98] transition-all"
              >
                <MIcon name="call" />
                {common?.callNow ?? CLINIC.phoneDisplay}
              </a>
              <a
                href={CLINIC.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-ds-success-green text-ds-clinical-white px-9 py-5 rounded-2xl font-bold text-[18px] shadow-xl hover:scale-[1.03] active:scale-[0.98] transition-all"
              >
                <MIcon name="chat" />
                {common?.whatsappCta}
              </a>
            </div>
            <Link
              to={hubPath}
              className="inline-flex items-center gap-2 text-ds-secondary font-semibold text-[15px] hover:underline"
            >
              <MIcon name="arrow_back" className="text-[18px]" />
              {common?.backToHub}
            </Link>
          </div>
        </section>
      )}

      {/* === Sticky Call-Bar (nur Mobile — Notfall-Traffic ist mobil) === */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-ds-clinical-white/95 backdrop-blur border-t border-ds-outline-variant/40 px-3 py-2 flex gap-2 shadow-[0_-4px_20px_rgba(27,38,59,0.12)]">
        <a
          href={`tel:${CLINIC.phone}`}
          className="flex-1 flex items-center justify-center gap-2 bg-ds-emergency-red text-ds-clinical-white py-3 rounded-xl font-bold text-[15px] active:scale-[0.98] transition-all"
        >
          <MIcon name="call" className="text-[20px]" />
          {CLINIC.phoneDisplay}
        </a>
        <a
          href={CLINIC.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          className="flex items-center justify-center bg-ds-success-green text-ds-clinical-white px-5 rounded-xl active:scale-[0.98] transition-all"
        >
          <MIcon name="chat" className="text-[22px]" />
        </a>
      </div>
    </main>
  );
};

export default EmergencySpokePage;
